/**
 * needle-bundle-add.js
 * <needle-bundle-add> custom element: combined "add to cart" for the current
 * product (e.g. the Dr. Pen Ultima M8 pen) plus a pack-size selection per
 * needle product, submitted as a single multi-item /cart/add.js request.
 *
 * Mirrors the theme's native ProductForm submit flow (assets/theme.js) so the
 * cart drawer/notification refresh exactly as they would for a normal add:
 * bundle the "variant-added" section via cart:prepare-bundled-sections, then
 * dispatch variant:add + cart:change with the merged cart payload.
 */

class NeedleBundleAdd extends HTMLElement {
  connectedCallback() {
    this._form = document.querySelector('form[action$="/cart/add"]');
    this._button = this.querySelector('[data-add-bundle]');
    this._button?.addEventListener('click', this._onAddBundle.bind(this));
  }

  async _onAddBundle() {
    if (!this._form || this._submitting) return;

    if (!this._form.checkValidity()) {
      this._form.reportValidity();
      return;
    }

    const penVariantId = this._form.elements['id']?.value;
    if (!penVariantId) return;

    const penQuantity = Number(this._form.elements['quantity']?.value) || 1;
    const items = [{ id: Number(penVariantId), quantity: penQuantity }];

    this.querySelectorAll('[data-needle-select]').forEach((select) => {
      if (select.value) items.push({ id: Number(select.value), quantity: 1 });
    });

    this._submitting = true;
    this._button.setAttribute('aria-busy', 'true');
    this._button.disabled = true;

    try {
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sectionsToBundle.join(',') }),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        this._form.dispatchEvent(
          new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson.description } })
        );
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      this._form.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: responseJson.items, cart: cartContent },
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent },
        })
      );
    } catch (err) {
      console.error('[needle-bundle-add] Failed to add to cart:', err);
    } finally {
      this._submitting = false;
      this._button.removeAttribute('aria-busy');
      this._button.disabled = false;
    }
  }
}

if (!customElements.get('needle-bundle-add')) {
  customElements.define('needle-bundle-add', NeedleBundleAdd);
}
